import React, { useEffect, useRef, useState } from 'react'
import styles from './ArtistGallery.module.css'

// Point directly to the Node.js API we created
const API_URL = '/api/artist-images'

// Optional helper to ensure the name is Title Cased
const formatArtistName = (name) => {
  return name.replace(/\b\w/g, (c) => c.toUpperCase()).trim()
}

const ArtistCard = ({ src, name, index }) => {
  const cardRef = useRef(null)
  const [visible, setVisible] = useState(false)
  const [imgLoaded, setImgLoaded] = useState(false)
  const [tilt, setTilt] = useState({ x: 0, y: 0 })
  const [hovered, setHovered] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.15 }
    )

    if (cardRef.current) observer.observe(cardRef.current)

    return () => observer.disconnect()
  }, [])

  const handleMouseMove = (e) => {
    const card = cardRef.current
    if (!card) return

    const rect = card.getBoundingClientRect()

    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 18
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -18

    setTilt({ x, y })
  }

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 })
    setHovered(false)
  }

  const delay = `${index * 80}ms`

  return (
    <div
      ref={cardRef}
      className={`${styles.card} ${visible ? styles.cardVisible : ''}`}
      style={{
        '--delay': delay,
        '--tilt-x': `${tilt.y}deg`,
        '--tilt-y': `${tilt.x}deg`,
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.cardInner}>
        {/* Glow ring */}
        <div
          className={`${styles.glowRing} ${hovered ? styles.glowRingActive : ''
            }`}
        />

        {/* Image wrapper */}
        <div className={styles.imageWrap}>
          {!imgLoaded && (
            <div className={styles.skeleton}>
              <div className={styles.skeletonShimmer} />
            </div>
          )}

          <img
            src={src}
            alt={name}
            className={`${styles.image} ${imgLoaded ? styles.imageLoaded : ''
              }`}
            onLoad={() => setImgLoaded(true)}
            loading="lazy"
          />
        </div>

        {/* Info */}
        <div className={styles.info}>
          <div className={styles.dot} />
          <span className={styles.artistName}>{name}</span>
        </div>
      </div>
    </div>
  )
}

const ArtistGallery = () => {
  const [artists, setArtists] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [showAll, setShowAll] = useState(false)

  const headerRef = useRef(null)
  const [headerVisible, setHeaderVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )

    if (headerRef.current) observer.observe(headerRef.current)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        // Fetch dynamically from your backend API
        const res = await fetch(API_URL)

        if (!res.ok) {
          throw new Error('Failed to fetch artists from server')
        }

        const data = await res.json()

        // Map backend JSON structure to component format
        const list = (data.images || []).map((item) => ({
          src: item.image,
          name: formatArtistName(item.artistName),
          filename: item.fileName,
        }))

        setArtists(list)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchArtists()
  }, [])

  // Show only first 8 initially
  const visibleArtists = showAll
    ? artists
    : artists.slice(0, 8)

  return (
    <section className={styles.section} id="artist-gallery">

      {/* Ambient BG blobs */}
      <div className={styles.blob1} />
      <div className={styles.blob2} />
      <div className={styles.blob3} />

      {/* Grain texture */}
      <div className={styles.grain} />

      <div className={styles.container}>

        {/* ── Header ── */}
        <div
          ref={headerRef}
          className={`${styles.header} ${headerVisible ? styles.headerVisible : ''
            }`}
        >
          <p className={styles.eyebrow}>
            <span className={styles.eyebrowLine} />

            The Stage Has Been Set

            <span className={styles.eyebrowLine} />
          </p>

          <h2 className={styles.title}>
            Past <em className={styles.titleItalic}>Performances</em>
          </h2>

          <p className={styles.subtitle}>
            Berli Brewing Co. has been home to some of Pune's most electrifying
            nights. Here's a look at the artists who've graced our stage.
          </p>

          <div className={styles.dividerWrap}>
            <div className={styles.divider} />
            <div className={styles.dividerDot} />
            <div className={styles.divider} />
          </div>
        </div>

        {/* ── Loading State ── */}
        {loading && (
          <div className={styles.skeletonGrid}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={styles.skeletonCard}>
                <div className={styles.skeletonImg}>
                  <div className={styles.skeletonShimmer} />
                </div>

                <div className={styles.skeletonBar}>
                  <div className={styles.skeletonShimmer} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Error State ── */}
        {error && (
          <div className={styles.errorState}>
            <span className={styles.errorIcon}>⚠️</span>
            <p>Could not load artists. Please try again later.</p>
          </div>
        )}

        {/* ── Empty State ── */}
        {!loading && !error && artists.length === 0 && (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>🎸</span>
            <p>No artists to display yet. Check back soon!</p>
          </div>
        )}

        {/* ── Grid ── */}
        {!loading && !error && artists.length > 0 && (
          <>
            <div className={styles.grid}>
              {visibleArtists.map((artist, i) => (
                <ArtistCard
                  key={artist.filename}
                  src={artist.src}
                  name={artist.name}
                  index={i}
                />
              ))}
            </div>

            {/* ── View All Button ── */}
            {!showAll && artists.length > 8 && (
              <div className={styles.viewAllWrap}>
                <button
                  type="button"
                  className={styles.viewAllButton}
                  onClick={() => setShowAll(true)}
                >
                  <span>View All Artists</span>
                  <span className={styles.viewAllArrow}>↓</span>
                </button>
              </div>
            )}
          </>
        )}

      </div>
    </section>
  )
}

export default ArtistGallery
